import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { SettingsSheet } from "./components/SettingsSheet";
import { DataStateView } from "./components/DataStateView";
import { useBriefing } from "./hooks/useBriefing";
import { useHashRoute, type RouteView } from "./hooks/useHashRoute";
import { usePreferences } from "./hooks/usePreferences";
import { AuditView } from "./views/AuditView";
import { BriefingView } from "./views/BriefingView";
import { CompareView } from "./views/CompareView";
import { SavedView } from "./views/SavedView";
import { SourcesView } from "./views/SourcesView";
import { StoryView } from "./views/StoryView";

const DebugView = lazy(() => import("./views/DebugView"));

export default function App() {
  const { briefing, source, error, retry } = useBriefing();
  const { route, navigate } = useHashRoute();
  const { preferences, setPreferences, toggleSaved } = usePreferences();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const brandTapCount = useRef(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: preferences.reduceMotion ? "auto" : "smooth" });
  }, [route.view, route.storyId, route.articleId, preferences.reduceMotion]);

  if (!briefing) {
    return <div className="app-shell" data-briefing-source={source}><div className="app-frame"><DataStateView state={source === "error" ? "error" : "loading"} error={error} onRetry={retry} /></div></div>;
  }

  const story = route.storyId ? briefing.stories.find((item) => item.id === route.storyId) : undefined;
  const fallbackStory = story ?? briefing.stories[0];
  const article = route.articleId ? fallbackStory?.articles.find((item) => item.id === route.articleId) : fallbackStory?.articles[0];
  const articleSource = article ? briefing.sources.find((item) => item.id === article.publisherId) : undefined;

  const navigatePrimary = (view: RouteView) => navigate({ view });
  const openStory = (storyId: string) => navigate({ view: "story", storyId });
  const brandTap = () => {
    brandTapCount.current += 1;
    if (brandTapCount.current >= 5) { brandTapCount.current = 0; navigate({ view: "debug" }); }
  };

  let view;
  if (route.view === "briefing") view = <BriefingView briefing={briefing} dataSource={source} refreshError={error} onRetry={retry} onStory={openStory} onNavigate={navigatePrimary} onSettings={() => setSettingsOpen(true)} onBrandTap={brandTap} />;
  else if (route.view === "compare") view = <CompareView briefing={briefing} onStory={openStory} onNavigate={navigatePrimary} onSettings={() => setSettingsOpen(true)} onBrandTap={brandTap} />;
  else if (route.view === "sources") view = <SourcesView briefing={briefing} onNavigate={navigatePrimary} onSettings={() => setSettingsOpen(true)} onBrandTap={brandTap} />;
  else if (route.view === "saved") view = <SavedView briefing={briefing} savedIds={preferences.savedStoryIds} onStory={openStory} onNavigate={navigatePrimary} onSettings={() => setSettingsOpen(true)} onBrandTap={brandTap} />;
  else if (route.view === "story" && fallbackStory) view = <StoryView story={fallbackStory} saved={preferences.savedStoryIds.includes(fallbackStory.id)} onBack={() => navigate({ view: "briefing" })} onSave={() => toggleSaved(fallbackStory.id)} onAudit={(articleId) => navigate({ view: "audit", storyId: fallbackStory.id, articleId })} />;
  else if (route.view === "audit" && fallbackStory && article) view = <AuditView article={article} source={articleSource} onBack={() => navigate({ view: "story", storyId: fallbackStory.id })} onDebug={() => navigate({ view: "debug" })} />;
  else if (route.view === "debug") view = <Suspense fallback={<div className="loading-screen">Loading audit trace…</div>}><DebugView article={article} onBack={() => article && fallbackStory ? navigate({ view: "audit", storyId: fallbackStory.id, articleId: article.id }) : navigate({ view: "briefing" })} /></Suspense>;
  else view = <BriefingView briefing={briefing} dataSource={source} refreshError={error} onRetry={retry} onStory={openStory} onNavigate={navigatePrimary} onSettings={() => setSettingsOpen(true)} onBrandTap={brandTap} />;

  return (
    <div className={`app-shell ${preferences.largeText ? "app-shell--large-text" : ""} ${preferences.reduceMotion ? "app-shell--reduce-motion" : ""}`} data-briefing-source={source}>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className="app-frame">{view}</div>
      {settingsOpen ? <SettingsSheet preferences={preferences} onChange={setPreferences} onClose={() => setSettingsOpen(false)} /> : null}
    </div>
  );
}
